import type {
  RechargeInitiatorRoleEnum,
  RechargeEntryEnum,
  RechargeStateEnum,
  RechargeConfirmActionEnum,
} from '#services/api';

/** 发起人角色 → 中文（映射 RechargeInitiatorRoleEnum） */
export const RECHARGE_ROLE_DESC: Record<RechargeInitiatorRoleEnum, string> = {
  PLANNER: '策划',
  OPTIMIZER: '优化师',
  SUPPLIER: '供应商',
};

/** 发起入口 → 中文（映射 RechargeEntryEnum） */
export const RECHARGE_ENTRY_DESC: Record<RechargeEntryEnum, string> = {
  QUOTE_INFO: '策划单报价信息',
  BUDGET_DASHBOARD: '预算管理看板',
  SUPPLIER: '供应商',
};

/** 充值单状态 → 中文（映射 RechargeStateEnum，含 Choice 伪状态） */
export const RECHARGE_STATUS_DESC: Record<RechargeStateEnum, string> = {
  PENDING_PLANNER_INITIATE: '待策划发起',
  PENDING_OPTIMIZER_INITIATE: '待优化师发起',
  PENDING_SUPPLIER_INITIATE: '待供应商发起',
  PLANNER_CONFIRM: '策划确认',
  OPTIMIZER_CONFIRM: '优化师确认',
  PRINCIPAL_CONFIRM: '策划负责人确认',
  SUPPLIER_CONFIRM: '供应商确认',
  COMPLETED: '已完成',
  ABANDONED: '已废弃',
  INITIATE_CHOICE: '发起后分流',
  PLANNER_CONFIRM_CHOICE: '策划确认后分流',
  REJECT_CHOICE: '退回分流',
};

/** 业务状态集合（排除 Choice 伪状态） */
export const RECHARGE_BUSINESS_STATUSES: RechargeStateEnum[] = [
  'PENDING_PLANNER_INITIATE',
  'PENDING_OPTIMIZER_INITIATE',
  'PENDING_SUPPLIER_INITIATE',
  'PLANNER_CONFIRM',
  'OPTIMIZER_CONFIRM',
  'PRINCIPAL_CONFIRM',
  'SUPPLIER_CONFIRM',
  'COMPLETED',
  'ABANDONED',
];

/** 确认节点状态（确认页按状态展示对应操作面板） */
export const RECHARGE_CONFIRM_STATUSES: RechargeStateEnum[] = [
  'PLANNER_CONFIRM',
  'OPTIMIZER_CONFIRM',
  'PRINCIPAL_CONFIRM',
  'SUPPLIER_CONFIRM',
];

/** 退回态（待 XX 发起，可废弃 / 重新发起） */
export const RECHARGE_PENDING_INITIATE_STATUSES: RechargeStateEnum[] = [
  'PENDING_PLANNER_INITIATE',
  'PENDING_OPTIMIZER_INITIATE',
  'PENDING_SUPPLIER_INITIATE',
];

/** 确认操作类型 → 中文（映射 RechargeConfirmActionEnum） */
export const RECHARGE_CONFIRM_ACTION_DESC: Record<RechargeConfirmActionEnum, string> = {
  CONFIRM: '确认通过',
  REJECT: '退回',
};

// ─── 字段规则常量（禁魔法数） ──────────────────────────────

/** 充值金额最小值 */
export const AMOUNT_MIN = 0.01;
/** 充值金额精度（小数位） */
export const AMOUNT_DECIMAL_PLACES = 2;
/** 核算实时预判 debounce 等待时长（ms）：用户停止输入后触发 */
export const CALC_PREVIEW_DEBOUNCE_WAIT = 500;
/** 原因类字段最大长度 */
export const REASON_MAX_LENGTH = 500;
/** 退回原因最大长度 */
export const REJECT_REASON_MAX_LENGTH = 200;
/** 批量确认上限（对齐后端 BATCH_SIZE_LIMIT=50，spec §5.6.4） */
export const BATCH_CONFIRM_MAX_SIZE = 50;
/** 附件单文件大小上限（200MB） */
export const ATTACHMENT_MAX_SIZE = 200 * 1024 * 1024;

// ─── 一级需求类型（l1_category_id）── 控制投放渠道/合作平台字段显隐 ─────

/** 效果买量 */
export const L1_CATEGORY_EFFECT_BUY = 19;
/** 收口投放 */
export const L1_CATEGORY_CONSTRAINED_DELIVERY = 20;
/** 品效推广 */
export const L1_CATEGORY_BRAND_EFFECT = 21;
/** 喇叭体系 */
export const L1_CATEGORY_TRUMPET = 22;

/** 显示「投放渠道」字段的一级需求类型（效果买量/收口投放/品效推广） */
export const CHANNEL_L1_CATEGORIES: ReadonlySet<number> = new Set([
  L1_CATEGORY_EFFECT_BUY,
  L1_CATEGORY_CONSTRAINED_DELIVERY,
  L1_CATEGORY_BRAND_EFFECT,
]);

/** 显示「合作平台」字段的一级需求类型（喇叭体系） */
export const PLATFORM_L1_CATEGORIES: ReadonlySet<number> = new Set([
  L1_CATEGORY_TRUMPET,
]);

// ─── filter_options key（optimizer_ids 选项走 CommonController#filter_options） ─────

/** 优化师选项 key（对齐后端 OptimizerUserOption.optionKey() = optimizer_user，按需求单产品反查 optimizer_config） */
export const FILTER_OPTION_KEY_RECHARGE_OPTIMIZER = 'optimizer_user';

// ─── 字段 key 常量 ─────────────────────────────────────

export const FIELD_KEYS = {
  L2_DEMAND_ID: 'l2_demand_id',
  L3_DEMAND_ID: 'l3_demand_id',
  SUPPLIER_NAME: 'supplier_name',
  ORDER_NO: 'order_no',
  ORDER_AMOUNT: 'order_amount',
  ACCOUNT_ID_NAME: 'account_id_name',
  CHANNEL: 'channel',
  PLATFORM: 'platform',
  BUDGET_AMOUNT: 'budget_amount',
  BUDGET_CURRENCY: 'budget_currency',
  QUOTE_PRICE: 'quote_price',
  QUOTE_CURRENCY: 'quote_currency',
  EXECUTE_DATE: 'execute_date',
  EXECUTE_DATE_START: 'execute_date_start',
  EXECUTE_DATE_END: 'execute_date_end',
  IS_OVER_CHARGE: 'is_over_charge',
  OVER_CHARGE_REASON: 'over_charge_reason',
  IS_EARLY_RECHARGE: 'is_early_recharge',
  EARLY_RECHARGE_REASON: 'early_recharge_reason',
  OPTIMIZER_IDS: 'optimizer_ids',
  ATTACHMENT: 'attachment',
  REJECT_REASON: 'reject_reason',
} as const;

/** 供应商端精简隐藏字段（后端 @JsonInclude NON_NULL 已过滤，前端按 SUPPLIER 角色隐藏） */
export const SUPPLIER_HIDDEN_FIELDS: ReadonlySet<string> = new Set([
  FIELD_KEYS.BUDGET_AMOUNT,
  FIELD_KEYS.QUOTE_PRICE,
  FIELD_KEYS.EXECUTE_DATE,
  FIELD_KEYS.IS_OVER_CHARGE,
  FIELD_KEYS.OVER_CHARGE_REASON,
  FIELD_KEYS.IS_EARLY_RECHARGE,
  FIELD_KEYS.EARLY_RECHARGE_REASON,
]);

/** 是否/否 选项（超充、提前充值只读展示用） */
export const BOOLEAN_OPTIONS = [
  { name: '是', value: true },
  { name: '否', value: false },
] as const;
